import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  HomeIcon,
  DocumentTextIcon,
  UserGroupIcon,
  KeyIcon,
  ChartBarIcon,
  FolderIcon,
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
  CpuChipIcon,
  WrenchScrewdriverIcon,
  ChatBubbleLeftRightIcon,
  CircleStackIcon,
  DocumentDuplicateIcon,
  PencilSquareIcon,
  CloudIcon,
  ServerStackIcon,
  PlusIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  HeartIcon,
  BeakerIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  BellAlertIcon,
  LifebuoyIcon,
  ClipboardDocumentCheckIcon,
  SunIcon,
  MoonIcon,
  UserIcon,
  ArrowUpOnSquareIcon,
  SparklesIcon,
  BookOpenIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { MapPinIcon as MapPinSolidIcon } from '@heroicons/react/24/solid';
import { MapPinIcon as MapPinOutlineIcon } from '@heroicons/react/24/outline';
import { useConsole } from '../../contexts/ConsoleContext';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import api, { analyticsAPI } from '../../services/api';
import { openServitiTeamSupport } from '../../utils/servitiTeamChat';
import ServitiLogo from '../common/ServitiLogo';

const Sidebar = ({ isOpen, isPlatformAdmin = false, onClose }) => {
  const location = useLocation();
  const { openConsole } = useConsole();
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const { theme, toggleTheme } = useThemeStore();
  const navigate = useNavigate();
  const [openSections, setOpenSections] = useState({});
  const { tenantId } = useParams();
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [missedOpportunities, setMissedOpportunities] = useState(null); // { enabled, missed_count } | null
  
  // Sidebar collapse state
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });
  const [isPinned, setIsPinned] = useState(() => {
    const saved = localStorage.getItem('sidebarPinned');
    return saved !== 'false'; // Default to pinned
  });
  const [isHovered, setIsHovered] = useState(false);
  
  // Save collapse/pin state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed);
  }, [isCollapsed]);
  
  useEffect(() => {
    localStorage.setItem('sidebarPinned', isPinned);
  }, [isPinned]);
  
  // Opening the drawer on a phone must always show the full navigation and
  // account controls, even if this user collapsed the desktop sidebar before.
  const showExpanded = isOpen || !isCollapsed || isHovered;

  // Fetch license info for sidebar display
  useEffect(() => {
    const fetchLicenseInfo = async () => {
      try {
        const response = await api.get('/license/check');
        setLicenseInfo(response.data);
      } catch {
        // Silently fail - license info is supplementary
      }
    };
    if (user) fetchLicenseInfo();
  }, [user?.tenant_id]);

  // Sidebar-badge entitlement check for Missed Opportunity Intelligence
  // (Scale, or a teased Growth tenant). One cheap COUNT-backed call per
  // tenant view, not the full dashboard summary -- this decides whether the
  // nav item renders at all, so a non-entitled tenant never sees it.
  useEffect(() => {
    const effectiveTenantId = isPlatformAdmin ? null : (tenantId || user?.tenant_id);
    if (!user || !effectiveTenantId) {
      setMissedOpportunities(null);
      return;
    }
    let active = true;
    analyticsAPI.getMissedOpportunitiesCount({ tenant_id: effectiveTenantId })
      .then((response) => {
        if (active) setMissedOpportunities(response.data);
      })
      .catch(() => {
        // Silently fail - the nav item just doesn't render.
        if (active) setMissedOpportunities(null);
      });
    return () => { active = false; };
  }, [isPlatformAdmin, tenantId, user?.tenant_id]);

  // Build tenant-prefixed path helper
  const buildPath = (path) => {
    if (isPlatformAdmin) {
      return `/platform${path}`;
    }
    if (tenantId) {
      return `/t/${tenantId}${path}`;
    }
    // Fallback to user's tenant_id
    if (user?.tenant_id) {
      return `/t/${user.tenant_id}${path}`;
    }
    return path;
  };

  // Role checks
  const isSuperadmin = user?.role === 'superadmin';
  const isTenantAdmin = user?.tenant_role === 'admin' || user?.tenant_role === 'owner';
  const isTenantUser = user?.tenant_role === 'member' || (!isSuperadmin && !isTenantAdmin);
  const displayedTier = licenseInfo?.subscription_tier || user?.subscription_tier || 'Plan';
  const planOverviewPath = isTenantAdmin
    ? buildPath('/billing')
    : buildPath('/plan-usage');

  // Build navigation based on role - Use useMemo to rebuild when tenantId changes
  const navigation = useMemo(() => {
    // Base navigation - available to ALL users
    const baseNav = [
      { name: 'Dashboard', href: buildPath('/dashboard'), icon: HomeIcon },
    ];

    // Always the last nav entry, appended at the end of every role's array below.
    const servitiSupportNavItem = {
      name: 'Get help',
      isButton: true,
      icon: ChatBubbleLeftRightIcon,
      onClick: () => {
        openServitiTeamSupport({
          tenantId: tenantId || user?.tenant_id,
          tenantName: user?.tenant_name,
          customerName: user?.full_name || user?.username,
          customerEmail: user?.email || user?.username,
        });
        onClose?.();
      },
    };

    // Shared tenant-admin nav builder. Used by both the tenant-admin branch
    // and the superadmin-impersonating-a-tenant branch below.
    const buildTenantAdminNav = (prepend = []) => [
      ...prepend,
      ...baseNav,
      {
        name: 'Customer enquiries',
        icon: ChartBarIcon,
        children: [
          { name: 'Leads', href: buildPath('/analytics'), icon: ChartBarIcon },
          { name: 'Conversations', href: buildPath('/conversations'), icon: ChatBubbleLeftRightIcon },
          { name: 'Support tickets', href: buildPath('/support'), icon: LifebuoyIcon },
          { name: 'Customer inbox', href: buildPath('/whatsapp-inbox'), icon: ChatBubbleLeftRightIcon },
        ],
      },
      {
        name: 'AI agents',
        icon: CpuChipIcon,
        children: [
          { name: 'My agents', href: buildPath('/agent-management'), icon: CpuChipIcon },
          { name: 'Create an agent', href: `${buildPath('/get-started')}?create=1`, icon: PlusIcon },
          { name: 'Business knowledge', href: buildPath('/rag/sources'), icon: BookOpenIcon },
          { name: 'Channels', href: buildPath('/whatsapp-outbound'), icon: ChatBubbleLeftRightIcon },
          { name: 'Launch checklist', href: buildPath('/go-live-checklist'), icon: ClipboardDocumentCheckIcon },
        ],
      },
      {
        name: 'Insights',
        icon: SparklesIcon,
        children: [
          { name: 'Business impact', href: buildPath('/analytics/impact'), icon: SparklesIcon },
          { name: 'Lead insights', href: buildPath('/lead-intelligence'), icon: ChartBarIcon },
          { name: 'Support insights', href: buildPath('/support-insights'), icon: LifebuoyIcon },
          {
            name: 'Missed opportunities',
            href: buildPath('/missed-opportunities'),
            icon: ChartBarIcon,
            badge: missedOpportunities?.enabled && missedOpportunities.missed_count > 0
              ? missedOpportunities.missed_count
              : undefined,
          },
        ],
      },
      {
        name: 'Team & notifications',
        icon: UserGroupIcon,
        children: [
          { name: 'Team members', href: buildPath('/users'), icon: UserGroupIcon },
          { name: 'Lead alerts', href: buildPath('/notification-settings'), icon: BellAlertIcon },
          { name: 'Support alerts', href: buildPath('/support-notifications'), icon: LifebuoyIcon },
        ],
      },
      {
        name: 'Account',
        icon: UserIcon,
        children: [
          { name: 'Plan & usage', href: buildPath('/plan-usage'), icon: ChartBarIcon },
          { name: 'Profile', href: buildPath('/my-profile'), icon: UserIcon },
        ],
      },
      // Top level, not inside a group. Paying is a decision, not a
      // configuration step, and it previously sat three clicks deep between
      // "Add User" and "My License". Tenant-admin nav only -- /billing is
      // RequireTenantAdmin, so a member following it from any other nav would
      // just be bounced back out. Pinned by
      // tests/test_tenant_billing_surface.py.
      { name: 'Billing & payments', href: buildPath('/billing'), icon: CreditCardIcon },
      servitiSupportNavItem,
    ];

    // Tenant User: simpler work-first view. Admin-only settings remain hidden.
    if (isTenantUser && !isTenantAdmin && !isSuperadmin) {
      return [
        ...baseNav,
        {
          name: 'Customer enquiries',
          icon: ChartBarIcon,
          children: [
            { name: 'Leads', href: buildPath('/analytics'), icon: ChartBarIcon },
            { name: 'Customer inbox', href: buildPath('/whatsapp-inbox'), icon: ChatBubbleLeftRightIcon },
            { name: 'Support tickets', href: buildPath('/support'), icon: LifebuoyIcon },
          ],
        },
        {
          name: 'AI agents',
          icon: CpuChipIcon,
          children: [
            { name: 'My agents', href: buildPath('/agent-management'), icon: CpuChipIcon },
            { name: 'Business knowledge', href: buildPath('/rag/sources'), icon: BookOpenIcon },
          ],
        },
        {
          name: 'Insights',
          icon: SparklesIcon,
          children: [
            { name: 'Business impact', href: buildPath('/analytics/impact'), icon: SparklesIcon },
            { name: 'Lead insights', href: buildPath('/lead-intelligence'), icon: ChartBarIcon },
            { name: 'Support insights', href: buildPath('/support-insights'), icon: LifebuoyIcon },
            {
              name: 'Missed opportunities',
              href: buildPath('/missed-opportunities'),
              icon: ChartBarIcon,
              badge: missedOpportunities?.enabled && missedOpportunities.missed_count > 0
                ? missedOpportunities.missed_count
                : undefined,
            },
          ],
        },
        {
          name: 'Account',
          icon: UserIcon,
          children: [
            { name: 'Plan & usage', href: buildPath('/plan-usage'), icon: ChartBarIcon },
            { name: 'Profile', href: buildPath('/my-profile'), icon: UserIcon },
          ],
        },
      ];
    }

    // Tenant Admin workspace navigation.
    if (isTenantAdmin && !isSuperadmin) {
      return buildTenantAdminNav();
    }

    // Superadmin: Full access to everything
    return [
      ...baseNav,
      {
        name: 'Tenant Onboarding',
        icon: BuildingOfficeIcon,
        children: [
          { name: 'Tenants', href: '/platform/tenants', icon: BuildingOfficeIcon },
          { name: 'Create Collection', href: buildPath('/rag/collections'), icon: CircleStackIcon },
          { name: 'Upload Knowledge', href: buildPath('/rag/upload'), icon: CloudArrowUpIcon },
          { name: 'Web Scraper', href: buildPath('/rag/web-scraper'), icon: GlobeAltIcon },
          { name: 'Knowledge Sources', href: buildPath('/rag/sources'), icon: BookOpenIcon },
          { name: 'Create Tenant AI Agent', href: buildPath('/agents'), icon: PlusIcon },
          { name: 'Channels', href: buildPath('/whatsapp-outbound'), icon: ChatBubbleLeftRightIcon },
          { name: 'Lead Notifications', href: buildPath('/notification-settings'), icon: BellAlertIcon },
          { name: 'Support Notifications', href: buildPath('/support-notifications'), icon: LifebuoyIcon },
          { name: 'Go-Live Checklist', href: buildPath('/go-live-checklist'), icon: ClipboardDocumentCheckIcon },
        ],
      },
      {
        name: 'Tenant Management',
        icon: ChartBarIcon,
        children: [
          { name: 'Leads', href: buildPath('/analytics'), icon: ChartBarIcon },
          { name: 'Conversations', href: buildPath('/conversations'), icon: ChatBubbleLeftRightIcon },
          { name: 'Support Tickets', href: buildPath('/support'), icon: LifebuoyIcon },
          { name: 'Customer inbox', href: buildPath('/whatsapp-inbox'), icon: ChatBubbleLeftRightIcon },
          { name: 'User Management', href: '/platform/users', icon: UserGroupIcon },
          { name: 'Plan & Usage', href: buildPath('/plan-usage'), icon: ChartBarIcon },
          { name: 'Licenses', href: buildPath('/licenses'), icon: KeyIcon },
          ...(isSuperadmin ? [{ name: 'License Generator', href: '/platform/license-generator', icon: PlusIcon }] : []),
          ...(isSuperadmin ? [{ name: 'Billing & Payments', href: '/platform/billing', icon: CreditCardIcon }] : []),
          ...(isSuperadmin ? [{ name: 'Payment Providers', href: '/platform/payment-providers', icon: CreditCardIcon }] : []),
          ...(isSuperadmin ? [{ name: 'Website Feedback', href: '/platform/website-feedback', icon: ChatBubbleLeftRightIcon }] : []),
          { name: 'Promote to Prod', href: '/platform/promote', icon: ArrowUpOnSquareIcon },
        ],
      },
      {
        name: 'Serviti AI Operations',
        icon: SparklesIcon,
        children: [
          { name: 'Agent Management', href: buildPath('/agent-management'), icon: CpuChipIcon },
          { name: 'Create Workflow', href: buildPath('/workflow-studio'), icon: PlusIcon },
          { name: 'Workflow Management', href: buildPath('/workflow-management'), icon: FolderIcon },
          { name: 'Prompt Editor', href: buildPath('/prompt-editor'), icon: PencilSquareIcon },
          { name: 'Fine-Tune', href: buildPath('/fine-tune'), icon: CpuChipIcon },
        ],
      },
      {
        name: 'Knowledge Ops',
        icon: FolderIcon,
        children: [
          { name: 'Knowledge Sources', href: buildPath('/rag/sources'), icon: BookOpenIcon },
          { name: 'Collections', href: buildPath('/rag/collections'), icon: CircleStackIcon },
          { name: 'Documents', href: buildPath('/rag/documents'), icon: DocumentDuplicateIcon },
          { name: 'Upload', href: buildPath('/rag/upload'), icon: CloudArrowUpIcon },
          { name: 'Web Scraper', href: buildPath('/rag/web-scraper'), icon: GlobeAltIcon },
          { name: 'CRM Sync', href: buildPath('/rag/crm-sync'), icon: CloudIcon },
          { name: 'DMS Sync', href: buildPath('/rag/dms-sync'), icon: ServerStackIcon },
          { name: 'Search', href: buildPath('/rag/search'), icon: MagnifyingGlassIcon },
          { name: 'Data Management', href: buildPath('/rag/data-management'), icon: FolderIcon },
          { name: 'Data Cleanup', href: buildPath('/rag/data-cleanup'), icon: BeakerIcon },
        ],
      },
      {
        name: 'Serviti AI Insights',
        icon: SparklesIcon,
        children: [
          { name: 'Serviti AI Impact', href: buildPath('/analytics/impact'), icon: SparklesIcon },
          { name: 'Serviti AI Lead Intelligence', href: buildPath('/lead-intelligence'), icon: SparklesIcon },
          { name: 'Serviti AI Support Insights', href: buildPath('/support-insights'), icon: LifebuoyIcon },
        ],
      },
      {
        name: 'Integrations',
        icon: ServerStackIcon,
        children: [
          { name: 'CRM Integration', href: buildPath('/crm-config'), icon: CloudIcon },
          { name: 'DMS Systems', href: buildPath('/dms-config'), icon: ServerStackIcon },
          { name: 'SharePoint', href: buildPath('/dms-config?tab=sharepoint'), icon: CloudIcon },
          { name: 'Google Drive', href: buildPath('/dms-config?tab=google_drive'), icon: FolderIcon },
          { name: 'AWS S3', href: buildPath('/dms-config?tab=s3'), icon: CircleStackIcon },
          { name: 'Confluence', href: buildPath('/dms-config?tab=confluence'), icon: DocumentDuplicateIcon },
        ],
      },
      {
        name: 'Diagnostics',
        icon: HeartIcon,
        children: [
          { name: 'Health Check', href: buildPath('/health-check'), icon: HeartIcon },
          { name: 'Platform Status', href: buildPath('/platform-status'), icon: ServerStackIcon },
          { name: 'Platform Config', href: buildPath('/configuration'), icon: WrenchScrewdriverIcon },
          // Who tried to sign up or sign in and did not get in. It belongs with
          // the other "is the platform working" screens rather than with
          // Tenant Management, which is about workspaces that already exist.
          ...(isSuperadmin ? [{ name: 'Signup Health', href: '/platform/signup-health', icon: UserIcon }] : []),
        ],
      },
      {
        name: 'Account',
        icon: UserIcon,
        children: [
          { name: 'My Profile', href: buildPath('/profile'), icon: UserIcon },
        ],
      },
      { name: 'Activity Console', isButton: true, icon: DocumentTextIcon, onClick: openConsole },
      // Sales demo (cold/warm prospect preview — public route, not tenant-scoped)
      {
        name: 'Sales Demo',
        icon: GlobeAltIcon,
        children: [
          { name: 'View as prospect', href: '/preview', icon: GlobeAltIcon },
          { name: 'Personalize for prospect', href: '/preview?edit=1', icon: PencilSquareIcon },
        ],
      },
    ];
  }, [tenantId, user?.email, user?.full_name, user?.tenant_id, user?.tenant_name, user?.tenant_role, user?.username, isSuperadmin, isTenantAdmin, isTenantUser, isPlatformAdmin, onClose, missedOpportunities]);

  // Prefer the most specific matching destination. This keeps a nested page
  // such as /agent-management/:id/prompt-pool inside "My agents", without
  // letting the shorter /analytics link steal /analytics/impact.
  const activeNavigationHref = useMemo(() => {
    const destinations = navigation.flatMap((item) => (
      item.children ? item.children.map((child) => child.href) : (item.href ? [item.href] : [])
    ));
    return destinations
      .map((href) => ({ href, path: href.split('?')[0] }))
      .filter(({ path }) => location.pathname === path || location.pathname.startsWith(`${path}/`))
      .sort((left, right) => right.path.length - left.path.length)[0]?.href;
  }, [location.pathname, navigation]);

  const isActive = (href) => href === activeNavigationHref;
  
  const isParentActive = (children) => {
    return children?.some((child) => isActive(child.href));
  };

  const isMobileViewport = () => {
    if (typeof window === 'undefined') return false;
    if (typeof window.matchMedia === 'function') {
      return window.matchMedia('(max-width: 1023px)').matches;
    }
    return window.innerWidth < 1024;
  };

  // A refresh, bookmark, browser Back/Forward, or programmatic navigation
  // must leave the current page visible in the sidebar. On phones the
  // navigation is an accordion so opening the active group also closes the
  // others and keeps the drawer short enough to scan.
  useEffect(() => {
    const activeSection = navigation.find(
      (item) => item.children?.some((child) => child.href === activeNavigationHref)
    );
    if (!activeSection) {
      if (isMobileViewport()) {
        setOpenSections((previous) => (
          Object.keys(previous).some((name) => previous[name]) ? {} : previous
        ));
      }
      return;
    }

    setOpenSections((previous) => {
      if (isMobileViewport()) {
        const alreadyOnlyActive = previous[activeSection.name]
          && Object.keys(previous).filter((name) => previous[name]).length === 1;
        return alreadyOnlyActive ? previous : { [activeSection.name]: true };
      }
      return previous[activeSection.name]
        ? previous
        : { ...previous, [activeSection.name]: true };
    });
  }, [activeNavigationHref, navigation]);

  const toggleSection = (sectionName) => {
    setOpenSections((previous) => {
      const willOpen = !previous[sectionName];
      if (willOpen && isMobileViewport()) return { [sectionName]: true };
      return { ...previous, [sectionName]: willOpen };
    });
  };



  const handleSignOut = () => {
    const tenantAdminTab = user?.tenant_admin_mode;
    logout();  // Clear auth state
    if (tenantAdminTab) {
      window.close();
      return;
    }
    navigate('/auth/login');  // Redirect to auth page
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={`${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } fixed inset-y-0 left-0 z-50 w-72 max-w-[calc(100vw-2rem)] ${
          showExpanded ? 'lg:w-64' : 'lg:w-16'
        } bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:max-w-none`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex flex-col h-full">
          {/* Mobile close button */}
          {onClose && (
            <button
              onClick={onClose}
              className="lg:hidden absolute top-3 right-3 z-50 p-1.5 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}
          {/* Logo and Collapse Button */}
          <div className="flex items-center justify-between h-20 px-2 border-b border-gray-200 dark:border-gray-700">
          {showExpanded ? (
            <ServitiLogo alt="Serviti AI Platform" className="h-12 flex-1" />
          ) : (
            <ServitiLogo alt="Serviti" compact className="mx-auto h-10 w-10" />
          )}
          {showExpanded && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsPinned(!isPinned)}
                className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
              >
                {isPinned ? (
                  <MapPinSolidIcon className="w-4 h-4 text-blue-600" />
                ) : (
                  <MapPinOutlineIcon className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {isCollapsed ? (
                  <ChevronDoubleRightIcon className="w-4 h-4" />
                ) : (
                  <ChevronDoubleLeftIcon className="w-4 h-4" />
                )}
              </button>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="min-h-0 flex-1 overflow-y-auto px-2 py-4 space-y-1">
          {navigation.map((item) => {
            if (item.children) {
              const parentActive = isParentActive(item.children);
              const isOpenSection = openSections[item.name];
              
              // Collapsed view - show only icon
              if (!showExpanded) {
                return (
                  <div key={item.name} className="relative group">
                    <button
                      className={`flex items-center justify-center p-2 rounded-md w-full ${
                        parentActive
                          ? 'text-indigo-600 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-900/50'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      title={item.name}
                    >
                      <item.icon className="w-5 h-5" />
                    </button>
                    {/* Tooltip */}
                    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                      {item.name}
                    </div>
                  </div>
                );
              }
              
              return (
                <div key={item.name} className="space-y-1">
                  <button
                    onClick={() => toggleSection(item.name)}
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-md w-full text-left ${
                      parentActive
                        ? 'text-indigo-600 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-900/50'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <item.icon className="w-5 h-5 mr-3" />
                    {item.name}
                    <svg
                      className={`w-4 h-4 ml-auto transition-transform ${isOpenSection ? 'rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  {isOpenSection && (
                    <div className="ml-8 space-y-1">
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={`flex items-center px-3 py-2 text-sm rounded-md ${
                            isActive(child.href)
                              ? 'text-indigo-600 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-900/50'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <child.icon className="w-4 h-4 mr-3" />
                          <span className="flex-1">{child.name}</span>
                          {child.badge && (
                            <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-200">
                              {child.badge}
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            if (item.isButton) {
              if (!showExpanded) {
                return (
                  <div key={item.name} className="relative group">
                    <button
                      onClick={item.onClick}
                      className="flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 w-full"
                      title={item.name}
                    >
                      <item.icon className="w-5 h-5" />
                    </button>
                    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                      {item.name}
                    </div>
                  </div>
                );
              }
              return (
                <button
                  key={item.name}
                  onClick={item.onClick}
                  className="flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 w-full text-left"
                >
                  <item.icon className="w-5 h-5 mr-3" />
                  {item.name}
                </button>
              );
            }

            // Regular link items
            if (!showExpanded) {
              return (
                <div key={item.name} className="relative group">
                  <Link
                    to={item.href}
                    className={`flex items-center justify-center p-2 rounded-md ${
                      isActive(item.href)
                        ? 'text-indigo-600 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-900/50'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                    title={item.name}
                  >
                    <item.icon className="w-5 h-5" />
                  </Link>
                  <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                    {item.name}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                  isActive(item.href)
                    ? 'text-indigo-600 bg-indigo-50 dark:text-indigo-400 dark:bg-indigo-900/50'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <item.icon className="w-5 h-5 mr-3" />
                <span className="flex-1">{item.name}</span>
                {item.badge && (
                  <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-200">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* License Status Badge */}
        {licenseInfo && !licenseInfo.message?.includes('Superadmin') && showExpanded && (
          <div className="px-3 py-2 mx-2 mb-2 rounded-lg bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/30 dark:to-blue-900/30 border border-indigo-100 dark:border-indigo-800">
            <Link to={planOverviewPath} className="block">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
                  {displayedTier}
                </span>
                {licenseInfo.valid ? (
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                    licenseInfo.days_remaining > 30
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400'
                      : licenseInfo.days_remaining > 0
                        ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'
                  }`}>
                    {licenseInfo.days_remaining > 0 ? `${licenseInfo.days_remaining}d left` : 'Expired'}
                  </span>
                ) : (
                  <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400">
                    Invalid
                  </span>
                )}
              </div>
              {licenseInfo.days_remaining != null && licenseInfo.days_remaining > 0 && (
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
                  <div
                    className={`h-1 rounded-full ${
                      licenseInfo.days_remaining > 30 ? 'bg-green-500' : licenseInfo.days_remaining > 7 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(100, (licenseInfo.days_remaining / 365) * 100)}%` }}
                  />
                </div>
              )}
            </Link>
          </div>
        )}
        {licenseInfo && !licenseInfo.message?.includes('Superadmin') && !showExpanded && (
          <div className="flex justify-center mb-2 relative group">
            <Link to={planOverviewPath} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
              licenseInfo.valid && licenseInfo.days_remaining > 30
                ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400'
                : licenseInfo.valid && licenseInfo.days_remaining > 0
                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400'
                  : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'
            }`}>
              {licenseInfo.days_remaining != null && licenseInfo.days_remaining > 0 ? licenseInfo.days_remaining : '!'}
            </Link>
            <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
              {displayedTier} - {licenseInfo.days_remaining > 0 ? `${licenseInfo.days_remaining} days left` : 'Expired'}
            </div>
          </div>
        )}

        {/* User Info — bottom padding respects env(safe-area-inset-bottom)
            so the Sign Out button doesn't sit under Android's gesture-nav /
            classic-nav bar (reported 2026-07-08). max() keeps the 0.5rem
            baseline on devices without a safe area (desktop, older browsers). */}
        <div className="shrink-0 px-2 pt-2 border-t border-gray-200 dark:border-gray-700" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
          {/* Theme Toggle */}
          {showExpanded ? (
            <button
              onClick={toggleTheme}
              className="flex items-center w-full px-3 py-2 mb-2 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {theme === 'dark' ? (
                <SunIcon className="w-5 h-5 mr-3 text-yellow-500" />
              ) : (
                <MoonIcon className="w-5 h-5 mr-3 text-gray-500" />
              )}
              {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </button>
          ) : (
            <div className="relative group">
              <button
                onClick={toggleTheme}
                className="flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 w-full mb-2 transition-colors"
                title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {theme === 'dark' ? (
                  <SunIcon className="w-5 h-5 text-yellow-500" />
                ) : (
                  <MoonIcon className="w-5 h-5 text-gray-500" />
                )}
              </button>
              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
                {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
              </div>
            </div>
          )}
          {showExpanded ? (
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user?.username?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                </div>
                <div className="ml-3 min-w-0">
                  <p className="truncate text-sm font-medium text-gray-700 dark:text-gray-300">
                    {user?.username || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {isSuperadmin ? 'Super Admin' : isTenantAdmin ? 'Admin' : 'Team member'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleSignOut}
                className="inline-flex min-h-[44px] shrink-0 items-center px-3 py-2 text-sm font-medium bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Sign Out
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center" title={user?.username || 'User'}>
                <span className="text-white text-sm font-medium">
                  {user?.username?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <button
                onClick={handleSignOut}
                className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Sign Out"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
